import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Shield, Fingerprint, AlertCircle, CheckCircle2, TrendingUp, TrendingDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const FRSDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [districtCoverage, setDistrictCoverage] = useState([]);
  const [authFunnel, setAuthFunnel] = useState(null);
  const [perfDistribution, setPerfDistribution] = useState(null);
  const [topBottom, setTopBottom] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('aadhaar_face_matched_pct');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedDistrict, selectedMetric]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [overviewRes, coverageRes, funnelRes, perfRes, topBottomRes, districtsRes] = await Promise.all([
        axios.get(`${API}/api/dashboards/frs/overview`),
        axios.get(`${API}/api/dashboards/frs/district-coverage?limit=50`),
        axios.get(`${API}/api/dashboards/frs/authentication-funnel${selectedDistrict !== 'all' ? `?district_id=${selectedDistrict}` : ''}`),
        axios.get(`${API}/api/dashboards/frs/performance-distribution`),
        axios.get(`${API}/api/dashboards/frs/top-bottom?metric=${selectedMetric}&limit=5`),
        axios.get(`${API}/api/dashboards/frs/districts`)
      ]);

      setOverview(overviewRes.data);
      setDistrictCoverage(coverageRes.data);
      setAuthFunnel(funnelRes.data);
      setPerfDistribution(perfRes.data);
      setTopBottom(topBottomRes.data);
      setDistricts(districtsRes.data);
    } catch (error) {
      console.error('Error fetching FRS dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (pct) => {
    if (pct >= 85) return '#22c55e';
    if (pct >= 70) return '#facc15';
    return '#ef4444';
  };

  const KPICard = ({ title, value, subtitle, icon: Icon, trend, highlight }) => (
    <Card className={highlight ? 'border-2 border-red-500' : ''}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-full ${
            trend === 'up' ? 'bg-green-100' : 
            trend === 'down' ? 'bg-red-100' : 
            highlight ? 'bg-orange-100' : 'bg-blue-100'
          }`}>
            <Icon className={`w-6 h-6 ${
              trend === 'up' ? 'text-green-600' : 
              trend === 'down' ? 'text-red-600' : 
              highlight ? 'text-orange-600' : 'text-blue-600'
            }`} />
          </div>
        </div>
        <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
        <p className="text-3xl font-bold" style={{ color: typeof value === 'string' && value.includes('%') ? getPerformanceColor(parseFloat(value)) : undefined }}>
          {value}
        </p>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        {highlight && <p className="text-xs text-red-600 mt-2 font-semibold">⚠️ Critical Issue</p>}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading FRS dashboard data...</p>
        </div>
      </div>
    );
  }

  // Prepare funnel data for visualization
  const funnelData = [
    { name: 'Eligible Beneficiaries', value: authFunnel?.eligible || 0, pct: 100, color: '#94a3b8' },
    { name: 'Aadhaar Face Matched', value: authFunnel?.aadhaar_matched || 0, pct: authFunnel?.aadhaar_matched_pct || 0, color: '#3b82f6' },
    { name: 'Face Captured THR', value: authFunnel?.face_captured_thr || 0, pct: authFunnel?.face_captured_pct || 0, color: '#f59e0b' },
    { name: 'Face Matched THR', value: authFunnel?.face_matched_thr || 0, pct: authFunnel?.face_matched_pct || 0, color: '#22c55e' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 pt-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Aadhaar & FRS Authentication Dashboard</h1>
          <p className="text-gray-600">Face Recognition System (FRS) for transparent beneficiary authentication and leakage prevention</p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
            <SelectTrigger className="w-64 bg-white">
              <SelectValue placeholder="Select District" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Districts</SelectItem>
              {districts.map(district => (
                <SelectItem key={district} value={district}>{district}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedMetric} onValueChange={setSelectedMetric}>
            <SelectTrigger className="w-64 bg-white">
              <SelectValue placeholder="Select Metric" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="aadhaar_face_matched_pct">Aadhaar Face Matching %</SelectItem>
              <SelectItem value="face_captured_thr_pct">Face Captured THR %</SelectItem>
              <SelectItem value="face_matched_thr_pct">Face Matched THR %</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            title="Aadhaar Face Matching"
            value={`${overview?.avg_aadhaar_face_matched_pct}%`}
            subtitle={`${overview?.total_beneficiaries?.toLocaleString()} beneficiaries`}
            icon={Fingerprint}
            trend={overview?.avg_aadhaar_face_matched_pct >= 85 ? 'up' : 'down'}
          />
          <KPICard
            title="Face Captured THR"
            value={`${overview?.avg_face_captured_thr_pct}%`}
            subtitle="During ration distribution"
            icon={AlertCircle}
            trend="down"
            highlight={overview?.avg_face_captured_thr_pct < 50}
          />
          <KPICard
            title="Face Matched THR"
            value={`${overview?.avg_face_matched_thr_pct}%`}
            subtitle="Successfully verified"
            icon={Shield}
            trend={overview?.avg_face_matched_thr_pct >= 70 ? 'up' : 'down'}
          />
          <KPICard
            title="Districts Below Target"
            value={overview?.districts_below_threshold}
            subtitle={`out of ${overview?.total_districts} districts`}
            icon={overview?.districts_below_threshold < 5 ? CheckCircle2 : AlertCircle}
            trend={overview?.districts_below_threshold < 5 ? 'up' : 'down'}
          />
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="districts">District Analysis</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="data">Data Table</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Authentication Funnel */}
              <Card>
                <CardHeader>
                  <CardTitle>Authentication Funnel</CardTitle>
                  <CardDescription>Beneficiary authentication flow from enrollment to verified delivery</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={funnelData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip 
                        formatter={(value, name, props) => [
                          `${value.toLocaleString()} (${props.payload.pct}%)`,
                          props.payload.name
                        ]}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {funnelData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  
                  {/* Critical Gap Alert */}
                  {authFunnel?.face_captured_pct < 50 && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-red-900">Critical Gap Detected</h4>
                          <p className="text-sm text-red-700 mt-1">
                            Only {authFunnel?.face_captured_pct}% face capture during THR distribution despite {authFunnel?.aadhaar_matched_pct}% having Aadhaar face matching. 
                            This represents a <strong>major leakage risk</strong> - {((authFunnel?.aadhaar_matched_pct - authFunnel?.face_captured_pct) / 100 * authFunnel?.eligible).toLocaleString()} beneficiaries 
                            lack verification during ration collection.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Performance Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance Distribution</CardTitle>
                  <CardDescription>Districts categorized by Aadhaar face matching %</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={perfDistribution?.distribution || []}
                        dataKey="count"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={(entry) => `${entry.category}: ${entry.count}`}
                      >
                        {perfDistribution?.distribution?.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {perfDistribution?.distribution?.map((item, idx) => (
                      <div key={idx} className="text-center p-3 rounded" style={{ backgroundColor: `${item.color}20` }}>
                        <p className="text-2xl font-bold" style={{ color: item.color }}>{item.count}</p>
                        <p className="text-xs text-gray-600 mt-1">{item.category}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Key Metrics Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Key Metrics Summary</CardTitle>
                <CardDescription>State-wide authentication statistics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total Districts</p>
                    <p className="text-2xl font-bold text-blue-700">{overview?.total_districts}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600">Beneficiaries Enrolled</p>
                    <p className="text-2xl font-bold text-green-700">{overview?.total_beneficiaries?.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-gray-600">Avg Aadhaar Match</p>
                    <p className="text-2xl font-bold text-purple-700">{overview?.avg_aadhaar_face_matched_pct}%</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <p className="text-sm text-gray-600">Avg Face Verified THR</p>
                    <p className="text-2xl font-bold text-orange-700">{overview?.avg_face_matched_thr_pct}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* District Analysis Tab */}
          <TabsContent value="districts" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Performers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Top 5 Performers
                  </CardTitle>
                  <CardDescription>Districts with highest {selectedMetric.replace(/_/g, ' ')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topBottom?.top_performers?.map((district, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium">{district.district}</p>
                          <p className="text-sm text-gray-600">{district.eligible_beneficiaries?.toLocaleString()} beneficiaries</p>
                        </div>
                        <div className="text-2xl font-bold text-green-700">{district.value}%</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Bottom Performers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    Bottom 5 Performers
                  </CardTitle>
                  <CardDescription>Districts needing immediate attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topBottom?.bottom_performers?.map((district, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{district.district}</p>
                          <p className="text-sm text-gray-600">{district.eligible_beneficiaries?.toLocaleString()} beneficiaries</p>
                        </div>
                        <div className="text-2xl font-bold text-red-700">{district.value}%</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <Card>
              <CardHeader>
                <CardTitle>District Performance - Aadhaar Face Matching %</CardTitle>
                <CardDescription>All districts sorted by performance</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={600}>
                  <BarChart data={districtCoverage.sort((a, b) => b.aadhaar_face_matched_pct - a.aadhaar_face_matched_pct)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="district" type="category" width={150} />
                    <Tooltip />
                    <Bar dataKey="aadhaar_face_matched_pct" name="Aadhaar Face Match %" radius={[0, 4, 4, 0]}>
                      {districtCoverage.map((entry, index) => (
                        <Cell key={index} fill={getPerformanceColor(entry.aadhaar_face_matched_pct)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Table Tab */}
          <TabsContent value="data">
            <Card>
              <CardHeader>
                <CardTitle>District-wise Complete Data</CardTitle>
                <CardDescription>All FRS authentication metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beneficiaries</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aadhaar Match %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Face Captured %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Face Matched %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {districtCoverage.map((district, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{district.district}</td>
                          <td className="px-4 py-3">{district.eligible_beneficiaries?.toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className="font-bold" style={{ color: getPerformanceColor(district.aadhaar_face_matched_pct) }}>
                              {district.aadhaar_face_matched_pct}%
                            </span>
                          </td>
                          <td className="px-4 py-3">{district.face_captured_thr_pct}%</td>
                          <td className="px-4 py-3">{district.face_matched_thr_pct}%</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              district.performance_rating === 'Excellent' ? 'bg-green-100 text-green-800' :
                              district.performance_rating === 'Good' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {district.performance_rating}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  </div>
  );
};

export default FRSDashboard;
